<template>
  <HelpCenterTeleport to="#help-center-errors">
    <div
      v-if="alert"
      :key="alert.uid"
      class="help-center-error__item"
    >
      <HelpCenterIcon
        name="alertOctagon"
        class="help-center-error__icon"
        size-units="5"
      />
      <div class="help-center-error__info">
        <p class="help-center-error__title">
          {{ alert.title }}
        </p>
        <p class="help-center-error__description">
          <template v-for="token in alert.tokens" :key="token.uid">
            <template v-if="token.text">
              {{ token.text }}
            </template>
            <a
              v-else
              rel="noopener"
              target="_blank"
              class="help-center-error__link"
              :href="token.href"
            >
              {{ token.name }}
            </a>
          </template>
        </p>
      </div>
    </div>
  </HelpCenterTeleport>
</template>

<script setup>
import { uniqueId } from 'gleam-shared/helpers/nodash'
import { ref, watch, toRef, onUnmounted } from 'vue'

import globalError from 'help-center/composables/useHelpCenterGlobalError'
import humanizeError from 'gleam-shared/network/humanizeError'
import { tokenizeMarkdown } from 'gleam-shared/helpers/markdown'

import HelpCenterIcon from 'help-center/components/HelpCenterIcon'
import HelpCenterTeleport from 'help-center/components/HelpCenterTeleport'

const DISMISS_TIMEOUT = 10000

const props = defineProps({
  main: Boolean,
  error: Object
})

let timer = null
const alert = ref(null)
const error = toRef(props, 'error')

if (props.main) {
  watch(globalError, showError, { immediate: true })
}

watch(error, passError, { immediate: true })

onUnmounted(() => {
  if (props.main) {
    clear()
  }
})

function clear() {
  clearTimeout(timer)
}

function passError(value) {
  if (!value) {
    return
  }

  globalError.value = value
}

function showError(value) {
  if (!value) {
    return
  }

  clear()

  alert.value = toAlert(value)
  timer = setTimeout(() => {
    alert.value = null
  }, DISMISS_TIMEOUT)
}

function toAlert(value) {
  const [title, markdown] = humanizeError(value)
  const tokens = tokenizeMarkdown(markdown)
  return { title, tokens, uid: uniqueId() }
}
</script>

<style lang="scss">
@import 'help-center/mixins';

$offset: 20px;
$padding: 16px;
$good-looking-width: 488px;

.help-center-error {
  &__item {
    @include help-center-shadow;

    position: fixed;
    z-index: 999;
    bottom: $offset;
    right: $offset;
    width: calc(min(#{$good-looking-width}, calc(100vw - #{$padding} * 2 - #{$offset})));

    display: flex;
    gap: 8px;

    border: 1px solid var(--help-center-border);
    padding: $padding;
    border-radius: 6px;
    background-color: white;
  }

  &__icon {
    color: var(--help-center-error);
    flex: none;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    @include help-center-font(15);
    color: var(--help-center-accent);
    font-weight: var(--help-center-bold);
  }

  &__description {
    @include help-center-font(14);
    color: var(--help-center-text);
    word-break: break-word;
  }

  &__link {
    display: inline;
    @include help-center-highlight-link($offset: 2px);
  }
}
</style>
